import React, { useRef, useState } from 'react';
import { Camera, Upload, X, Image as ImageIcon, Loader2, Crop, RefreshCw } from 'lucide-react';
import { Button } from './button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './dialog';
import { ImageCropper } from './ImageCropper';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Standard aspect ratios for the app (matching mobile)
export const IMAGE_FORMATS = {
  PROFILE: { aspectRatio: [1, 1], maxSize: 400, shape: 'circle' },
  SERVICE: { aspectRatio: [16, 9], maxSize: 800, shape: 'rectangle' },
  COURSE: { aspectRatio: [16, 9], maxSize: 800, shape: 'rectangle' },
  COVER: { aspectRatio: [3, 2], maxSize: 1000, shape: 'rectangle' },
};

// Translations for the component
const translations = {
  es: {
    processing: 'Procesando...',
    uploading: 'Subiendo...',
    change: 'Cambiar',
    cameraOrLibrary: 'Cámara o Galería',
    cameraOrBrowse: 'Cámara o Buscar Archivos',
    addPhoto: 'Añadir Foto',
    takePhoto: 'Tomar Foto',
    useWebcam: 'Usar Webcam',
    chooseFromLibrary: 'Elegir de Galería',
    browseFiles: 'Buscar Archivos',
    removePhoto: 'Eliminar Foto',
    capture: 'Capturar',
    cancel: 'Cancelar',
    uploadError: 'Error al subir la imagen. Inténtalo de nuevo.',
    frontCamera: 'Cámara Frontal',
    rearCamera: 'Cámara Trasera',
    switchCamera: 'Cambiar Cámara',
    previewNote: 'Tu foto se mostrará completa, sin recortes',
  },
  en: {
    processing: 'Processing...',
    uploading: 'Uploading...',
    change: 'Change',
    cameraOrLibrary: 'Camera or Photo Library',
    cameraOrBrowse: 'Camera or Browse Files',
    addPhoto: 'Add Photo',
    takePhoto: 'Take Photo',
    useWebcam: 'Use Webcam',
    chooseFromLibrary: 'Choose from Library',
    browseFiles: 'Browse Files',
    removePhoto: 'Remove Photo',
    capture: 'Capture',
    cancel: 'Cancel',
    uploadError: 'Error uploading image. Please try again.',
    frontCamera: 'Front Camera',
    rearCamera: 'Rear Camera',
    switchCamera: 'Switch Camera',
    previewNote: 'Your photo will be displayed in full, without cropping',
  }
};

export function ImageUpload({
  image,
  onImageChange,
  label = 'Add Image',
  aspectRatio = [16, 9],
  maxSize = 800,
  shape = 'rectangle',
  placeholder = 'Tap to add photo',
  language = 'es',
  folder = 'uploads',
  authToken = null,
  showPreview = true,
  enableCrop = true,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showOptions, setShowOptions] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [tempImageUrl, setTempImageUrl] = useState(null);
  const [tempImageFile, setTempImageFile] = useState(null);
  const [useFrontCamera, setUseFrontCamera] = useState(false); // false = rear camera (environment), true = front camera (user)
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null); // Separate ref for camera input
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // Check if we're on mobile
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  
  // Get translations
  const t = translations[language] || translations.es;

  // Upload file to Cloudinary using signed upload
  const uploadToCloudinary = async (file) => {
    try {
      // Get auth token from localStorage if not provided
      const token = authToken || localStorage.getItem('token');
      
      if (!token) {
        // Fallback to base64 for non-authenticated users (shouldn't happen in normal flow)
        console.warn('No auth token, falling back to base64');
        return await processImageToBase64(file);
      }

      // Get signature from backend
      const sigResponse = await axios.get(`${API_URL}/api/cloudinary/signature`, {
        params: { folder, resource_type: 'image' },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const { signature, timestamp, cloud_name, api_key, folder: signedFolder } = sigResponse.data;

      // Prepare form data for Cloudinary upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('api_key', api_key);
      formData.append('timestamp', timestamp);
      formData.append('signature', signature);
      formData.append('folder', signedFolder);

      // Upload directly to Cloudinary
      const uploadResponse = await axios.post(
        `https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`,
        formData,
        {
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          }
        }
      );

      // Return the secure URL from Cloudinary
      return uploadResponse.data.secure_url;
    } catch (error) {
      console.error('Cloudinary upload error:', error);
      throw error;
    }
  };

  // Process image to optimized blob for upload
  const processImageForUpload = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Calculate dimensions maintaining aspect ratio
          let width = maxSize;
          let height = Math.round(maxSize * (aspectRatio[1] / aspectRatio[0]));
          
          // For profile (circle), make it square
          if (shape === 'circle') {
            height = maxSize;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          // Draw and crop to center
          const sourceAspect = img.width / img.height;
          const targetAspect = width / height;
          
          let sx = 0, sy = 0, sw = img.width, sh = img.height;
          
          if (sourceAspect > targetAspect) {
            sw = img.height * targetAspect;
            sx = (img.width - sw) / 2;
          } else {
            sh = img.width / targetAspect;
            sy = (img.height - sh) / 2;
          }
          
          ctx.drawImage(img, sx, sy, sw, sh, 0, 0, width, height);
          
          // Convert canvas to blob
          canvas.toBlob((blob) => {
            if (blob) {
              resolve(new File([blob], file.name || 'image.jpg', { type: 'image/jpeg' }));
            } else {
              reject(new Error('Failed to create blob'));
            }
          }, 'image/jpeg', 0.85);
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Fallback: process image to base64 (for non-authenticated users)
  const processImageToBase64 = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          let width = maxSize;
          let height = Math.round(maxSize * (aspectRatio[1] / aspectRatio[0]));
          if (shape === 'circle') height = maxSize;
          
          canvas.width = width;
          canvas.height = height;
          
          const sourceAspect = img.width / img.height;
          const targetAspect = width / height;
          
          let sx = 0, sy = 0, sw = img.width, sh = img.height;
          
          if (sourceAspect > targetAspect) {
            sw = img.height * targetAspect;
            sx = (img.width - sw) / 2;
          } else {
            sh = img.width / targetAspect;
            sy = (img.height - sh) / 2;
          }
          
          ctx.drawImage(img, sx, sy, sw, sh, 0, 0, width, height);
          resolve(canvas.toDataURL('image/jpeg', 0.7));
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Close options dialog first
    setShowOptions(false);
    
    // If crop is enabled and it's not a circle (profile pic), show cropper
    if (enableCrop && shape !== 'circle') {
      // Create a temporary URL for the cropper
      const tempUrl = URL.createObjectURL(file);
      setTempImageUrl(tempUrl);
      setTempImageFile(file);
      // Small delay to ensure dialog is closed before opening cropper
      setTimeout(() => {
        setShowCropper(true);
      }, 100);
      return;
    }
    
    // Otherwise, process directly
    await processAndUploadImage(file);
  };

  const processAndUploadImage = async (file) => {
    setIsLoading(true);
    setUploadProgress(0);
    try {
      // Process image (resize/crop)
      const processedFile = await processImageForUpload(file);
      // Upload to Cloudinary
      const imageUrl = await uploadToCloudinary(processedFile);
      onImageChange(imageUrl);
    } catch (error) {
      console.error('Error processing/uploading image:', error);
      alert(t.uploadError);
    } finally {
      setIsLoading(false);
      setUploadProgress(0);
    }
  };

  const handleCropSave = async (croppedFile, croppedDataUrl) => {
    setShowCropper(false);
    // Ensure body scroll is restored
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.overflow = '';
    
    if (tempImageUrl) {
      URL.revokeObjectURL(tempImageUrl);
      setTempImageUrl(null);
    }
    
    if (croppedFile) {
      await processAndUploadImage(croppedFile);
    } else if (croppedDataUrl) {
      // If we only got a data URL, convert it to a file
      const response = await fetch(croppedDataUrl);
      const blob = await response.blob();
      const file = new File([blob], 'cropped-image.jpg', { type: 'image/jpeg' });
      await processAndUploadImage(file);
    }
  };

  const handleCropCancel = () => {
    setShowCropper(false);
    if (tempImageUrl) {
      URL.revokeObjectURL(tempImageUrl);
      setTempImageUrl(null);
      setTempImageFile(null);
    }
    // Ensure body scroll is restored
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.overflow = '';
  };

  const startCamera = async (frontCamera = useFrontCamera) => {
    setShowOptions(false);
    setShowCamera(true);
    try {
      // Stop any existing stream first
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
      
      const facingMode = frontCamera ? 'user' : 'environment';
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: isMobile ? facingMode : 'user' } 
      });
      setCameraStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      alert('Could not access camera. Please check permissions.');
      setShowCamera(false);
    }
  };

  // Switch between front and rear camera
  const switchCamera = async () => {
    const newUseFrontCamera = !useFrontCamera;
    setUseFrontCamera(newUseFrontCamera);
    await startCamera(newUseFrontCamera);
  };

  const capturePhoto = async () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    setIsLoading(true);
    setUploadProgress(0);
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Set canvas dimensions
    let width = maxSize;
    let height = Math.round(maxSize * (aspectRatio[1] / aspectRatio[0]));
    if (shape === 'circle') height = maxSize;
    
    canvas.width = width;
    canvas.height = height;
    
    // Draw video frame
    const videoAspect = video.videoWidth / video.videoHeight;
    const targetAspect = width / height;
    
    let sx = 0, sy = 0, sw = video.videoWidth, sh = video.videoHeight;
    
    if (videoAspect > targetAspect) {
      sw = video.videoHeight * targetAspect;
      sx = (video.videoWidth - sw) / 2;
    } else {
      sh = video.videoWidth / targetAspect;
      sy = (video.videoHeight - sh) / 2;
    }
    
    ctx.drawImage(video, sx, sy, sw, sh, 0, 0, width, height);
    
    // Convert canvas to blob and upload to Cloudinary
    try {
      const blob = await new Promise((resolve) => 
        canvas.toBlob(resolve, 'image/jpeg', 0.85)
      );
      const file = new File([blob], 'camera-capture.jpg', { type: 'image/jpeg' });
      const imageUrl = await uploadToCloudinary(file);
      onImageChange(imageUrl);
    } catch (error) {
      console.error('Error uploading camera capture:', error);
      alert(t.uploadError);
    } finally {
      stopCamera();
      setIsLoading(false);
      setUploadProgress(0);
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setShowCamera(false);
  };

  const removeImage = () => {
    onImageChange(null);
    setShowOptions(false);
  };

  const containerStyle = shape === 'circle' 
    ? 'w-36 h-36 rounded-full mx-auto' 
    : 'w-full aspect-video rounded-2xl';

  return (
    <div className="mb-5">
      {label && <label className="block text-sm font-semibold text-primary mb-2">{label}</label>}
      
      <div
        className={`${containerStyle} border-2 border-dashed border-gray-200 bg-cream-dark overflow-hidden cursor-pointer hover:border-gold transition-colors relative`}
        style={{ backgroundColor: '#F8F6F3' }}
        onClick={() => !isLoading && setShowOptions(true)}
        data-testid="image-upload-container"
      >
        {isLoading ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" style={{ borderColor: '#D4A574', borderTopColor: 'transparent' }} />
            <span className="text-sm text-secondary mt-2">
              {uploadProgress > 0 ? `${t.uploading} ${uploadProgress}%` : t.processing}
            </span>
            {uploadProgress > 0 && (
              <div className="w-32 h-1.5 bg-gray-200 rounded-full mt-2 overflow-hidden">
                <div 
                  className="h-full rounded-full transition-all duration-300" 
                  style={{ width: `${uploadProgress}%`, backgroundColor: '#D4A574' }} 
                />
              </div>
            )}
          </div>
        ) : image && showPreview ? (
          <div className="relative w-full h-full group">
            <img 
              src={image} 
              alt="Uploaded" 
              className={`w-full h-full object-contain ${shape === 'circle' ? 'rounded-full object-cover' : ''}`}
              style={{ backgroundColor: '#F5EFE6' }}
            />
            <div className="absolute inset-x-0 bottom-0 bg-black/50 py-2 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <Camera className="w-5 h-5 text-white" />
              <span className="text-white text-sm font-medium">{t.change}</span>
            </div>
          </div>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center p-5">
            <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center shadow-sm mb-3">
              <Camera className="w-8 h-8 text-gold" style={{ color: '#D4A574' }} />
            </div>
            <span className="text-base font-semibold text-primary">{placeholder}</span>
            <span className="text-sm text-secondary mt-1">
              {isMobile ? t.cameraOrLibrary : t.cameraOrBrowse}
            </span>
          </div>
        )}
      </div>
      
      {/* Preview note - shows when shape is rectangle */}
      {shape === 'rectangle' && (
        <p className="text-xs text-secondary mt-2 text-center flex items-center justify-center gap-1">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {t.previewNote}
        </p>
      )}

      {/* Options Dialog */}
      <Dialog open={showOptions} onOpenChange={setShowOptions}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-primary">{t.addPhoto}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-4">
            {/* Camera option - shows webcam preview for both mobile and desktop */}
            <Button
              onClick={() => startCamera(false)}
              className="w-full justify-start gap-3 h-12 bg-white hover:bg-gray-50 border border-gray-200 text-primary"
              variant="outline"
              data-testid="camera-option"
            >
              <Camera className="w-5 h-5" style={{ color: '#D4A574' }} />
              <span>{isMobile ? `${t.takePhoto} (${t.rearCamera})` : t.useWebcam}</span>
            </Button>
            
            {/* Front camera option - only on mobile */}
            {isMobile && (
              <Button
                onClick={() => startCamera(true)}
                className="w-full justify-start gap-3 h-12 bg-white hover:bg-gray-50 border border-gray-200 text-primary"
                variant="outline"
                data-testid="front-camera-option"
              >
                <Camera className="w-5 h-5" style={{ color: '#D4A574' }} />
                <span>{t.takePhoto} ({t.frontCamera})</span>
              </Button>
            )}
            
            <Button
              onClick={() => fileInputRef.current?.click()}
              className="w-full justify-start gap-3 h-12 bg-white hover:bg-gray-50 border border-gray-200 text-primary"
              variant="outline"
              data-testid="gallery-option"
            >
              <Upload className="w-5 h-5" style={{ color: '#D4A574' }} />
              <span>{isMobile ? t.chooseFromLibrary : t.browseFiles}</span>
            </Button>
            
            {image && (
              <Button
                onClick={removeImage}
                variant="destructive"
                className="w-full justify-start gap-3 h-12"
                data-testid="remove-image-option"
              >
                <X className="w-5 h-5" />
                <span>{t.removePhoto}</span>
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Camera Dialog */}
      <Dialog open={showCamera} onOpenChange={(open) => !open && stopCamera()}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-primary flex items-center justify-between">
              <span>{t.takePhoto}</span>
              {isMobile && (
                <span className="text-xs font-normal text-secondary">
                  {useFrontCamera ? t.frontCamera : t.rearCamera}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="relative bg-black rounded-lg overflow-hidden aspect-video">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
              />
              {/* Switch camera button overlay (mobile only) */}
              {isMobile && (
                <button
                  onClick={switchCamera}
                  className="absolute top-3 right-3 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-colors"
                  aria-label={t.switchCamera}
                  data-testid="switch-camera-btn"
                >
                  <RefreshCw className="w-5 h-5" />
                </button>
              )}
            </div>
            <canvas ref={canvasRef} className="hidden" />
            <div className="flex gap-3">
              <Button
                onClick={stopCamera}
                variant="outline"
                className="flex-1 border-gray-200 text-primary"
              >
                {t.cancel}
              </Button>
              <Button
                onClick={capturePhoto}
                className="flex-1 text-white"
                style={{ backgroundColor: '#D4A574' }}
                data-testid="capture-photo-btn"
              >
                <Camera className="w-5 h-5 mr-2" />
                {t.capture}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Hidden file input for gallery/files */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
        data-testid="file-input"
      />
      
      {/* Hidden camera input for direct mobile capture (rear camera) */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture={useFrontCamera ? 'user' : 'environment'}
        onChange={handleFileSelect}
        className="hidden"
        data-testid="camera-input"
      />

      {/* Image Cropper Modal */}
      {showCropper && tempImageUrl && (
        <ImageCropper
          imageUrl={tempImageUrl}
          onSave={handleCropSave}
          onCancel={handleCropCancel}
          aspectRatio={aspectRatio}
          language={language}
        />
      )}
    </div>
  );
}

export default ImageUpload;
